import React from 'react';
import { GradeLegendBody, LithLegendBody } from './EditableGradeLegend';
import { useClickStore } from '../../store/clickStore';
import { useBlockModelFilterStore } from '../../store/blockModelFilterStore';
import { useHoleDisplayStore } from '../../store/holeDisplayStore';

function layerLabel(modelList, name) {
  for (const section of modelList || []) {
    const model = (section.models || []).find((m) => m.name === name);
    if (model) return model.displayName || model.label || model.name;
  }
  return name;
}

function OverlayCard({ title, subtitle, onClose, children }) {
  return (
    <div className="pointer-events-auto w-[11.5rem] rounded-sm border border-yellow-600/40 bg-black/90 shadow-lg backdrop-blur-sm px-2 py-1.5">
      <div className="flex items-start gap-1 mb-1.5">
        <div className="min-w-0 flex-1">
          <p className="text-[9px] uppercase tracking-wider text-yellow-500/90">{title}</p>
          {subtitle ? (
            <p className="text-[10px] text-zinc-300 truncate" title={subtitle}>
              {subtitle}
            </p>
          ) : null}
        </div>
        <button
          type="button"
          title="Close legend"
          onClick={onClose}
          className="m-0 h-4 w-4 shrink-0 text-zinc-500 hover:text-yellow-400"
        >
          ✕
        </button>
      </div>
      {children}
    </div>
  );
}

export default function HoleLegendStack({ modelList = [] }) {
  const legendOpenByLayer = useHoleDisplayStore((s) => s.legendOpenByLayer);
  const holesLegendOpen = useHoleDisplayStore((s) => s.holesLegendOpen);
  const closeLegend = useHoleDisplayStore((s) => s.closeLegend);
  const setHolesLegendOpen = useHoleDisplayStore((s) => s.setHolesLegendOpen);
  const blockModelLegendOpen = useBlockModelFilterStore((s) => s.legendOpen);
  const setBlockModelLegendOpen = useBlockModelFilterStore((s) => s.setLegendOpen);
  const lithLegends = useClickStore((s) => s.lithLegends);

  const modelsByName = new Map();
  (modelList || []).forEach((section) => {
    (section.models || []).forEach((model) => modelsByName.set(model.name, model));
  });

  const openNames = Object.keys(legendOpenByLayer || {}).filter((name) => legendOpenByLayer[name]);
  const holeLithCodes = [];
  const seenLith = new Set();
  Object.keys(lithLegends || {}).forEach((name) => {
    const model = modelsByName.get(name);
    if (!model || String(model.type || '').toLowerCase() !== 'databaseholes') return;
    (lithLegends[name] || []).forEach((code) => {
      const key = String(code || '').trim();
      if (!key || seenLith.has(key)) return;
      seenLith.add(key);
      holeLithCodes.push(key);
    });
  });

  const blockLithCodes = [];
  const seenBlockLith = new Set();
  Object.keys(lithLegends || {}).forEach((name) => {
    const model = modelsByName.get(name);
    if (!model || String(model.type || '').toLowerCase() !== 'blockmodel') return;
    (lithLegends[name] || []).forEach((code) => {
      const key = String(code || '').trim();
      if (!key || seenBlockLith.has(key)) return;
      seenBlockLith.add(key);
      blockLithCodes.push(key);
    });
  });

  if (!openNames.length && !holesLegendOpen && !blockModelLegendOpen) return null;

  return (
    <div className="absolute bottom-3 right-3 z-[58] flex flex-col gap-1.5 items-end pointer-events-none max-h-[40vh] max-w-[min(14rem,calc(100%-1.25rem))] overflow-y-auto">
      {blockModelLegendOpen && (
        <OverlayCard title="Block models" onClose={() => setBlockModelLegendOpen(false)}>
          <GradeLegendBody compact />
          {blockLithCodes.length > 0 && (
            <div className="mt-1.5 pt-1.5 border-t border-yellow-600/20">
              <p className="text-[9px] uppercase tracking-wider text-zinc-500 mb-1">Lithology</p>
              <LithLegendBody codes={blockLithCodes} hideable={false} />
            </div>
          )}
        </OverlayCard>
      )}
      {holesLegendOpen && (
        <OverlayCard title="Holes" onClose={() => setHolesLegendOpen(false)}>
          <p className="text-[9px] uppercase tracking-wider text-zinc-500 mb-1">Assay</p>
          <GradeLegendBody compact />
          {holeLithCodes.length > 0 && (
            <div className="mt-1.5 pt-1.5 border-t border-yellow-600/20">
              <p className="text-[9px] uppercase tracking-wider text-zinc-500 mb-1">Geology</p>
              <LithLegendBody codes={holeLithCodes} />
            </div>
          )}
        </OverlayCard>
      )}
      {openNames.map((name) => {
        const lithCodes = lithLegends?.[name] || [];
        return (
          <OverlayCard
            key={name}
            title="Holes"
            subtitle={layerLabel(modelList, name)}
            onClose={() => closeLegend(name)}
          >
            <p className="text-[9px] uppercase tracking-wider text-zinc-500 mb-1">Assay</p>
            <GradeLegendBody compact />
            {lithCodes.length > 0 && (
              <div className="mt-1.5 pt-1.5 border-t border-yellow-600/20">
                <p className="text-[9px] uppercase tracking-wider text-zinc-500 mb-1">Geology</p>
                <LithLegendBody codes={lithCodes} />
              </div>
            )}
          </OverlayCard>
        );
      })}
    </div>
  );
}
